import React, { ReactNode, useState } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { InputField } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { TertiaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tertiaryButton/TertiaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

const Container = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['24px'],
}))

const Header = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
})

const FormSection = styled.View(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['32px'],
}))

const ButtonRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: theme.spacing['16px'],
}))

export type TaskModalContentsProps = {
  title: ReactNode
  initialDescription?: string
  primaryButtonText: ReactNode
  onPrimaryPress: (description: string) => Promise<void>
  secondaryButton?: {
    text: ReactNode
    onPress: () => Promise<void>
  }
  onClose: () => void
  testID?: string
}

export const TaskModalContents: React.FC<TaskModalContentsProps> = ({
  title,
  initialDescription,
  primaryButtonText,
  onPrimaryPress,
  secondaryButton,
  onClose,
  testID = 'TaskModal',
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()
  const [description, setDescription] = useState(initialDescription ?? '')
  const [primaryLoading, setPrimaryLoading] = useState(false)
  const [secondaryLoading, setSecondaryLoading] = useState(false)

  const isPrimaryDisabled = description.trim() === '' || secondaryLoading

  const handlePrimary = async () => {
    setPrimaryLoading(true)
    try {
      await onPrimaryPress(description.trim())
      onClose()
    } catch {
      setPrimaryLoading(false)
    }
  }

  const handleSecondary = async () => {
    if (!secondaryButton) {
      return
    }
    setSecondaryLoading(true)
    try {
      await secondaryButton.onPress()
      onClose()
    } catch {
      setSecondaryLoading(false)
    }
  }

  return (
    <Container testID={tID(`${testID}-container`)}>
      <Header>
        <Title size={TitleSize.MEDIUM} text={title} />
        <PressableOpacity
          onPress={onClose}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Close modal',
            description: 'Accessibility label for task modal close button',
          })}
          testID={tID(`${testID}-close`)}
        >
          <XIcon size={24} fillColor={colors.iconDefault} />
        </PressableOpacity>
      </Header>
      <FormSection>
        <InputField
          label={formatMessage({
            defaultMessage: 'Description of task *',
            description: 'Required text field label for task description',
          })}
          value={description}
          onChange={(e) => setDescription(e.nativeEvent.text)}
          baseInputStyle={{ marginBottom: 0 }}
          testID={tID(`${testID}-description`)}
        />
        <ButtonRow>
          {secondaryButton && (
            <TertiaryButton
              text={secondaryButton.text}
              onPress={handleSecondary}
              disabled={primaryLoading}
              loading={secondaryLoading}
              testID={tID(`${testID}-secondary`)}
            />
          )}
          <PrimaryButton
            text={primaryButtonText}
            onPress={handlePrimary}
            disabled={isPrimaryDisabled}
            loading={primaryLoading}
            testID={tID(`${testID}-primary`)}
          />
        </ButtonRow>
      </FormSection>
    </Container>
  )
}
